// src/oura/components/onboarding/OnboardingStrip.tsx
//
// The top of the staff Client tab while a client is being set up. It replaces
// the tab's old "Start a plan" and "Create merchant login" buttons: onboarding
// reads the same signed quote, and sends the login once the first AI check is
// done, so the tab only needs to say where the client stands and link there.
//
// Renders nothing for a client whose set-up is finished, or who was set up
// before onboarding existed.

import { useEffect, useState } from "react";
import { RocketIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { IconSquare, PillButton, PillLink, Surface, Tag, TYPE } from "@oura/ui";
import { getOnboarding, type Onboarding } from "@service/aeo/onboarding";
import { getEngagementIfExists } from "@service/aeo/clientReview";
import { callName, nextAction, onboardingProgress } from "../../onboarding/derive";
import { NewClientDialog } from "./NewClientDialog";
import { OURA_JOINED_SIGNING_ENABLED, SIGN_PRESS } from "../../newscan/flags";

type State =
    | { kind: "loading" }
    | { kind: "onboarding"; record: Onboarding }
    /** No onboarding record and no plan: a prospect who has not signed. */
    | { kind: "prospect" }
    /** Nothing to show: finished, set up before onboarding existed, or the read failed. */
    | { kind: "none" };

export function OnboardingStrip({
    businessId,
    business,
    onboardingHref,
}: {
    businessId: string;
    business: { name: string; domain: string };
    onboardingHref: string;
}) {
    const [state, setState] = useState<State>({ kind: "loading" });
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setState({ kind: "loading" });
        (async () => {
            try {
                const record = await getOnboarding(businessId);
                if (cancelled) return;
                if (record) {
                    setState(nextAction(record).kind === "done" ? { kind: "none" } : { kind: "onboarding", record });
                    return;
                }
                // Only a real 404 is "no plan". Any other failure lands in the
                // catch below, which shows nothing rather than offer to start.
                const plan = await getEngagementIfExists(businessId);
                if (!cancelled) setState(plan ? { kind: "none" } : { kind: "prospect" });
            } catch {
                // The Onboarding tab says the read failed and offers a retry.
                if (!cancelled) setState({ kind: "none" });
            }
        })();
        return () => { cancelled = true; };
    }, [businessId]);

    if (state.kind === "loading" || state.kind === "none") return null;

    if (state.kind === "prospect") {
        return (
            <>
                <Surface className="!py-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <IconSquare icon={RocketIcon} tone="orange" />
                        <div className="min-w-0 flex-1 basis-48">
                            <p className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>Not a client yet</p>
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                                {/* Off, the sheet records a paid date: the press waits for the payment, as on main. */}
                                {OURA_JOINED_SIGNING_ENABLED ? "Press Client signed when the quote is signed." : "Start onboarding once the quote is signed and paid."}
                            </p>
                        </div>
                        <PillButton data-start-onboarding="" className="max-sm:h-9" onClick={() => setStarting(true)}>
                            {SIGN_PRESS}
                        </PillButton>
                    </div>
                </Surface>
                <NewClientDialog
                    open={starting}
                    onClose={() => setStarting(false)}
                    existing={{ businessId, name: business.name, domain: business.domain }}
                    onCreated={({ onboarding }) => setState({ kind: "onboarding", record: onboarding })}
                    onOpen={() => setStarting(false)}
                    returnFocus={() => document.querySelector<HTMLElement>("[data-start-onboarding]")}
                />
            </>
        );
    }

    const o = state.record;
    const next = nextAction(o);
    const { done, total } = onboardingProgress(o);
    return (
        <Surface className="!py-4">
            <div className="flex flex-wrap items-center gap-3">
                <IconSquare icon={RocketIcon} tone="orange" />
                <div className="min-w-0 flex-1 basis-48">
                    <p className={cn(TYPE.tb, "m-0 flex flex-wrap items-center gap-2 font-semibold text-gray-900")}>
                        Onboarding, {done} of {total}
                        <Tag tone={next.tag?.tone ?? (next.who === "staff" ? "blue" : next.who === "oura" ? "violet" : "amber")}>
                            {next.tag?.label ?? (next.waitingOn ? `Needs ${callName(next.waitingOn)}` : next.who === "staff" ? "Needs us" : "Oura is working")}
                        </Tag>
                    </p>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{next.title}</p>
                </div>
                <PillLink to={onboardingHref} variant="secondary" className="max-sm:h-9">
                    Open onboarding
                </PillLink>
            </div>
        </Surface>
    );
}
